// App.jsx — DROP-IN
import React, { useEffect, useRef, useState } from "react";
import "./App.css";
import { useNavigate, Routes, Route, Navigate } from "react-router-dom";
import Detail from "./Detail";
import MainTable from "./MainTable";
import Login from "./Login";
import { checkAuth, clearClientState } from "./Auth";
import Loader from "./Loader";
import { startSSE } from "./SSE";
import GlobalAlerts from "./GlobalAlerts";
import { getSessionUser } from "./Auth";
import { ForcePasswordChangeDialog } from "./Login";

function PasswordResetGateInline() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");

  useEffect(() => {
    let active = true;

    // Proactive check on first paint (covers hard refresh)
    (async () => {
      try {
        const me = await getSessionUser();
        if (!active) return;
        if (me?.must_change_password) {
          setEmail(me.email || "");
          setOpen(true);
          try { window.dispatchEvent(new CustomEvent("hide-login")); } catch {}
        }
      } catch {/* ignore */}
    })();

    // Open on any 428s emitted by fetchJson
    const onReq = (e) => {
      if (e?.detail?.email) setEmail(e.detail.email);
      setOpen(true);
      try { window.dispatchEvent(new CustomEvent("hide-login")); } catch {}
      document.body.style.overflow = "hidden";
    };
    const onDone = () => {
      setOpen(false);
      document.body.style.overflow = "";
      window.location.reload();
    };

    window.addEventListener("password-change-required", onReq);
    window.addEventListener("password-change-completed", onDone);
    return () => {
      active = false;
      window.removeEventListener("password-change-required", onReq);
      window.removeEventListener("password-change-completed", onDone);
    };
  }, []);

  if (!open) return null;
  return (
    <ForcePasswordChangeDialog
      email={email}
      onDone={() => {
        try { window.dispatchEvent(new CustomEvent("password-change-completed")); } catch {}
      }}
    />
  );
}


function App() {
  const navigate = useNavigate();

  const [tableState, setTableState] = useState({
    rowData: [],
    isLoading: false,
    pages: 0,
    count: 0,
    currentPage: 1,
    currentVRMFilter: "",
    currentStartDateTime: null,
    currentEndDateTime: null,
  });

  // "unknown" (checking) | "authed" | "unauth"
  const [authState, setAuthState] = useState("unknown");

  // Ensure we don't start multiple SSE connections
  const sseStopRef = useRef(null);

  // Initial auth check on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await checkAuth();
        if (!cancelled) setAuthState("authed");
      } catch {
        if (!cancelled) setAuthState("unauth");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Listen for global 401 notifications from fetchJson (data.js)
  useEffect(() => {
    const onUnauth = () => setAuthState("unauth");
    window.addEventListener("unauthenticated", onUnauth);
    return () => window.removeEventListener("unauthenticated", onUnauth);
  }, []);

  // 🔑 Start/stop SSE based on auth state (and only once)
  useEffect(() => {
    if (authState !== "authed") {
      // If we log out or become unauth, make sure SSE is closed
      if (sseStopRef.current) {
        try { sseStopRef.current(); } catch { }
        sseStopRef.current = null;
      } else {
        // Fire a soft "disconnect" for SSE module that doesn't expose a stop fn
        window.dispatchEvent(new CustomEvent("sse:disconnect"));
      }
      try {clearClientState();} catch {}
      return;
    }

    // Already connected? (prevents duplicate connections in StrictMode/dev)
    if (sseStopRef.current) return;

    // Start stream; prefer a cleanup function if startSSE provides one
    const maybeStop = startSSE?.();
    if (typeof maybeStop === "function") {
      sseStopRef.current = maybeStop;
    } else {
      sseStopRef.current = null;
    }

    // Cleanup on unmount (or when authState flips away from authed)
    return () => {
      if (sseStopRef.current) {
        try { sseStopRef.current(); } catch { }
        sseStopRef.current = null;
      } else {
        window.dispatchEvent(new CustomEvent("sse:disconnect"));
      }
    };
  }, [authState]);

  if (authState === "unknown") {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
        <Loader />
      </div>
    );
  }

  return (
    <div>
      <GlobalAlerts />
      <PasswordResetGateInline />
      <Routes>
        {authState === "unauth" ? (
          <>
            <Route
              path="/login"
              element={
                <Login
                  onSuccess={async () => {
                    try {
                      await checkAuth();
                      setAuthState("authed");
                      navigate("/", { replace: true });
                    } catch {
                      // remain on login
                    }
                  }}
                />
              }
            />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </>
        ) : (
          <>
            <Route
              path="/"
              element={<MainTable tableState={tableState} setTableState={setTableState} />}
            />
            <Route path="/detail/:id" element={<Detail />} />
            <Route path="/login" element={<Navigate to="/" replace />} />
          </>
        )}
      </Routes>
    </div>
  );
}

export default App;
